import { useCallback, useEffect, useRef, useState } from "react";
import {
  loadNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type AppNotification
} from "../apiClient.js";
import { Avatar } from "./Avatar.js";
import { useT } from "../lib/i18n/store.js";

/** Maps a notification type to its i18n message key. */
const MESSAGE_KEY = {
  comment: "notifications.comment",
  reply: "notifications.reply",
  like: "notifications.like"
} as const;

const POLL_INTERVAL_MS = 60_000;

/**
 * Topbar notification inbox: a bell with an unread badge and a dropdown of the
 * author's recent notifications. Owns its own data — polls on an interval and
 * refetches when opened. A load failure is silent (an empty bell), since
 * notifications are not core to using the app.
 */
export function NotificationBell({ onOpenNovel }: { onOpenNovel?: (novelId: string) => void }) {
  const t = useT();
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  // False until the first load settles: a dropdown opened before then shows
  // placeholder rows, not "No notifications yet." that the answer may contradict.
  const [loaded, setLoaded] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Monotonic request sequence: every load and every local mutation (mark read /
  // mark all read) bumps it, so a response from a load that started BEFORE the
  // latest one — or before a mark-all-read — is discarded instead of overwriting
  // newer state with stale unread flags.
  const seqRef = useRef(0);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    const seq = ++seqRef.current;
    try {
      const result = await loadNotifications();
      if (!mountedRef.current) return;
      setLoaded(true);
      if (seq !== seqRef.current) return;
      setItems(result.notifications);
      setUnread(result.unreadCount);
    } catch {
      // Silent: the bell just shows nothing new.
      if (mountedRef.current) setLoaded(true);
    }
  }, []);

  // Poll on mount and every minute so the badge stays roughly current. The
  // interval is cleared (and late responses ignored) on unmount.
  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(timer);
    };
  }, [refresh]);

  // Dismiss the dropdown on an outside click or Escape (mirrors the nav menu).
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) void refresh();
  }

  async function handleClick(notification: AppNotification) {
    setOpen(false);
    if (!notification.read) {
      seqRef.current += 1; // invalidate any in-flight load
      setItems((current) => current.map((n) => (n.id === notification.id ? { ...n, read: true } : n)));
      setUnread((count) => Math.max(0, count - 1));
      try {
        const seq = seqRef.current;
        const count = await markNotificationRead(notification.id);
        if (mountedRef.current && seq === seqRef.current) setUnread(count);
      } catch {
        // Keep the optimistic state; the next poll reconciles.
      }
    }
    onOpenNovel?.(notification.novel_id);
  }

  async function handleMarkAll() {
    seqRef.current += 1; // a load started before this must not resurrect unread items
    setItems((current) => current.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try {
      await markAllNotificationsRead();
    } catch {
      void refresh();
    }
  }

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className="notif-trigger"
        aria-label={t("notifications.aria")}
        aria-expanded={open}
        onClick={toggle}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 && <span className="notif-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div className="notif-menu" role="menu">
          <div className="notif-menu-head">
            <span>{t("notifications.title")}</span>
            {unread > 0 && (
              <button type="button" className="notif-markall" onClick={() => void handleMarkAll()}>
                {t("notifications.markAllRead")}
              </button>
            )}
          </div>
          {!loaded ? (
            <ul className="notif-list" aria-hidden="true">
              {[0, 1, 2].map((index) => (
                <li key={index}>
                  <div className="notif-item notif-item--skeleton">
                    <span className="skeleton notif-avatar notif-skel-avatar" />
                    <span className="skeleton notif-skel-text" />
                  </div>
                </li>
              ))}
            </ul>
          ) : items.length === 0 ? (
            <p className="notif-empty">{t("notifications.empty")}</p>
          ) : (
            <ul className="notif-list">
              {items.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    className={notification.read ? "notif-item" : "notif-item notif-item--unread"}
                    onClick={() => void handleClick(notification)}
                  >
                    <Avatar
                      name={notification.actor.display_name}
                      url={notification.actor.avatar_url}
                      size={28}
                      className="notif-avatar"
                    />
                    <span className="notif-text">
                      {t(MESSAGE_KEY[notification.type], {
                        name: notification.actor.display_name,
                        title: notification.novel_title ?? t("notifications.aNovel")
                      })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
